import type { WorkspaceAction } from '../../utils/workspacePermissions'
import { httpsCallable } from 'firebase/functions'
import { firebaseFunctions } from '../../services/firebase'
import { findUnauthorizedLocalizedLabel } from './LocalizationConstants'
import { getAnchor } from './tourAnchorManifest'
import { validateTour } from './TourRegistry'
import type { TourAction, TourDefinition, TourStep } from './types'

const ACTIONS = new Set<TourAction>(['click', 'input', 'wait'])
const POSITIONS = new Set(['top', 'right', 'bottom', 'left', 'auto'])

const extractAnchorId = (selector: string): string | null => {
  const match = selector.match(/^\[data-tour-id="([^"]+)"\]$/)
  return match ? match[1] : null
}

const sanitizeText = (value: unknown, max: number) => {
  if (typeof value !== 'string') return ''
  return value
    .replace(/<[^>]*>/g, '')
    .replace(/[\u0000-\u001f\u007f]/g, ' ')
    .replace(/\[[^\]]+\]\([^)]+\)/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, max)
}

const sanitizeId = (value: unknown, fallback: string) => {
  const source = typeof value === 'string' ? value : fallback
  const cleaned = source.toLowerCase().replace(/[^a-z0-9._-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 80)
  return cleaned || fallback
}

const assertAllowedUiCopy = (value: string) => {
  const unauthorized = findUnauthorizedLocalizedLabel(value)
  if (unauthorized) throw new Error(`The generated tour references an unauthorized UI label: ${unauthorized}.`)
}

const randomSuffix = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID().slice(0, 8)
    : Math.random().toString(36).slice(2, 10)

export const logTourSanitizerAnomaly = async (input: {
  workspaceId: string
  tourId?: string
  reason: string
  source?: string
  anchorsUsed?: string[]
}) => {
  const call = httpsCallable<typeof input, { ok: boolean }>(firebaseFunctions, 'recordTourSanitizerAnomaly')
  await call({
    workspaceId: input.workspaceId,
    tourId: input.tourId,
    reason: input.reason.slice(0, 500),
    source: input.source,
    anchorsUsed: input.anchorsUsed?.slice(0, 20),
  })
}

export const sanitizeTourDefinitionForRuntime = (
  value: unknown,
  context: { canPerform: (action: WorkspaceAction) => boolean },
): TourDefinition => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('The generated tour is not a valid object.')
  }
  const candidate = value as Record<string, unknown>
  const rawSteps = Array.isArray(candidate.steps) ? candidate.steps.slice(0, 9) : []
  if (!rawSteps.length) throw new Error('The generated tour has no playable steps.')

  const steps = rawSteps.map((rawStep, index): TourStep => {
    if (!rawStep || typeof rawStep !== 'object' || Array.isArray(rawStep)) {
      throw new Error(`Tour step ${index + 1} is invalid.`)
    }
    const step = rawStep as Record<string, unknown>
    const targetSelector = sanitizeText(step.targetSelector, 140)
    const anchorId = extractAnchorId(targetSelector)
    const anchor = anchorId ? getAnchor(anchorId) : undefined
    if (!anchorId || !anchor) throw new Error('The generated tour references an unavailable screen control.')
    if (anchor.requiredAction && !context.canPerform(anchor.requiredAction)) {
      throw new Error('The generated tour includes a workflow you do not have permission to access.')
    }
    const action = typeof step.action === 'string' && ACTIONS.has(step.action as TourAction) ? step.action as TourAction : undefined
    if (action && !anchor.validActions.includes(action)) {
      throw new Error('The generated tour includes an unsupported interaction.')
    }
    if (sanitizeText(step.screenPath, 200) !== anchor.screenPath) {
      throw new Error('The generated tour references an invalid screen path.')
    }
    const sanitizedStep: TourStep = {
      id: sanitizeId(step.id, `step-${index + 1}`),
      title: sanitizeText(step.title, 80),
      message: sanitizeText(step.message, 500),
      targetSelector: `[data-tour-id="${anchorId}"]`,
      screenPath: anchor.screenPath,
      ...(typeof step.position === 'string' && POSITIONS.has(step.position) ? { position: step.position as TourStep['position'] } : {}),
      ...(action ? { action } : {}),
    }
    if (!sanitizedStep.title || !sanitizedStep.message) throw new Error('The generated tour is missing step copy.')
    assertAllowedUiCopy(sanitizedStep.title)
    assertAllowedUiCopy(sanitizedStep.message)
    if (action === 'wait') {
      sanitizedStep.durationMs = Math.max(0, Math.min(10_000, Math.floor(Number(step.durationMs) || 0)))
    }
    if (action === 'input') {
      const selector = sanitizeText(step.selector, 140)
      const inputAnchorId = extractAnchorId(selector)
      const inputAnchor = inputAnchorId ? getAnchor(inputAnchorId) : undefined
      if (!inputAnchorId || !inputAnchor) throw new Error('The generated tour references an unavailable input.')
      if (inputAnchor.requiredAction && !context.canPerform(inputAnchor.requiredAction)) {
        throw new Error('The generated tour includes a workflow you do not have permission to access.')
      }
      sanitizedStep.selector = `[data-tour-id="${inputAnchorId}"]`
      sanitizedStep.criteria = sanitizeText(step.criteria, 120)
      if (!sanitizedStep.criteria) throw new Error('The generated tour is missing input criteria.')
    }
    return sanitizedStep
  })

  const id = sanitizeId(candidate.id, `ai-${randomSuffix()}`)
  const tour: TourDefinition = {
    id: id.startsWith('ai-') ? id : `ai-${id}`,
    title: sanitizeText(candidate.title, 80) || undefined,
    description: sanitizeText(candidate.description, 240) || undefined,
    steps,
  }
  if (tour.title) assertAllowedUiCopy(tour.title)
  if (tour.description) assertAllowedUiCopy(tour.description)
  const validated = validateTour(tour, tour.id, 'strict')
  if (!validated) throw new Error('The generated tour failed runtime validation.')
  return validated
}
